/**
 * Speech usage tokens: pieces spoken while an answer was still streaming are
 * requested without its (not yet known) stored answer id, so each
 * `POST /api/agent/speech` NDJSON stream ends with an opaque `usage` token
 * instead of a booked cost. Once the answer's stored `messageId` is known,
 * every collected token is booked through the GraphQL mutation
 * `bookAgentSpeechUsage` (`{ token, messageId }`, one call per token —
 * booking is idempotent, so a retry is harmless) to attribute its cost. This
 * module only decides which collected tokens still need that call; the
 * actual fire-and-forget calls live in `agentSpeechOutput.ts`.
 *
 * Adapted from the MEIS-13490 prototype's `agentSpeechBooking.ts`: the
 * product's `bookAgentSpeechUsage` mutation books one token per call (not an
 * array of up to 50 ids per call as the prototype's endpoint did), so this
 * module tracks per-token booked state instead of chunking a raw id list.
 */

export type SpeechUsageToken = {
  /** Opaque; identifies one `/api/agent/speech` NDJSON stream's usage line. */
  token: string;
  costUsd: number;
  promptTokens: number;
  audioTokens: number;
};

/**
 * `tokens` with `next` appended, replacing any earlier entry for the same
 * opaque token (kept in its original position moved to the end — in
 * practice a token is only ever reported once, this just avoids a
 * duplicate if a piece's stream were ever retried).
 */
export function collectSpeechUsage(
  tokens: readonly SpeechUsageToken[],
  next: SpeechUsageToken
): SpeechUsageToken[] {
  return [...tokens.filter(t => t.token !== next.token), next];
}

/** Which collected tokens still need a `bookAgentSpeechUsage` mutation call. */
export function pendingSpeechTokens(
  tokens: readonly SpeechUsageToken[],
  bookedTokens: ReadonlySet<string>
): SpeechUsageToken[] {
  return tokens.filter(t => !bookedTokens.has(t.token));
}

/**
 * The summed cost of every collected token — the live, not-yet-booked
 * speech cost shown while an answer streams (`agentSpeechCost.ts`'s
 * `liveSpeechCostUsd`).
 */
export function sumSpeechUsageCost(
  tokens: readonly SpeechUsageToken[]
): number {
  return tokens.reduce((sum, t) => sum + t.costUsd, 0);
}
